import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import {
  CarePlanPickerData,
  CarePlanPickerGoal,
  CarePlanPickerPlan,
  CarePlanWorkRecord,
} from 'src/app/models/ccm/care-plan-work.model';
import {
  CarePlan,
  CarePlanAction,
  CarePlanProblem,
  CarePlanState,
  CarePlanTask,
} from 'src/app/models/ccm/ccm-data-model';
import { environment } from 'src/environments/environment';
import { FirestoreService } from '../firestore.service';
import { interventionLines, isOpenGoal, mergeFrozenPicks, toRequestPlans } from './care-plan-work.utils';

/**
 * What the CCM care plan picker shows (BRD-CLIN-2026-008 FR-3): the patient's Active care plans with
 * their open goals and interventions, and — when an entry is edited — the picks saved on it.
 *
 * Every read is `colWithIdsOnce$`, a server `get()`: not `colWithIds$().pipe(take(1))`, whose first
 * emission comes from the local cache and can be a partial subset while the chart's own care plan
 * listeners are open. A plan edited elsewhere while the time form is open does not reshape the picker
 * either; the server checks the picks against today's plans anyway.
 *
 * Errors are not swallowed: a picker that cannot load must not pass as "no care plan" or "no picks",
 * which would let an edit silently replace the picks stored on the entry.
 */
@Injectable({
  providedIn: 'root',
})
export class CarePlanWorkService {
  constructor(
    private http: HttpClient,
    private fs: FirestoreService
  ) {}

  /** Everything the picker of one time row needs. `timeId` is the entry being edited, if any. */
  loadPicker$(patientId: string, timeId?: string): Observable<CarePlanPickerData> {
    return forkJoin([this.getActivePlans$(patientId), timeId ? this.getRecord$(patientId, timeId) : of(null)]).pipe(
      map(([plans, record]) => ({ plans: mergeFrozenPicks(plans, record), value: toRequestPlans(record), record }))
    );
  }

  /** Active plans only, as everywhere else (`state == 'Active'`), one section per plan, by name. */
  getActivePlans$(patientId: string): Observable<CarePlanPickerPlan[]> {
    if (!patientId) {
      return of([]);
    }
    return this.fs
      .colWithIdsOnce$<CarePlan>('clinical_care_plans', (ref) =>
        ref.where('id_patient', '==', patientId).where('state', '==', CarePlanState.ACTIVE)
      )
      .pipe(
        switchMap((plans: CarePlan[]) =>
          plans.length ? forkJoin(plans.map((plan) => from(this.toPickerPlan(plan)))) : of([])
        ),
        map((plans: CarePlanPickerPlan[]) => [...plans].sort((a, b) => a.name.localeCompare(b.name)))
      );
  }

  /** The picks frozen on one entry, or `null` when it has none. */
  getRecord$(patientId: string, timeId: string): Observable<CarePlanWorkRecord | null> {
    const url = `${environment.welbyEndpoint}/api/v1/core/patients/${encodeURIComponent(
      patientId
    )}/clinical-time/${encodeURIComponent(timeId)}/care-plan-work`;
    return this.http.get<ApiResponse<CarePlanWorkRecord | null>>(url).pipe(map((res) => res?.data ?? null));
  }

  private async toPickerPlan(plan: CarePlan): Promise<CarePlanPickerPlan> {
    const problems: CarePlanProblem[] = await this.once<CarePlanProblem>(`clinical_care_plans/${plan.id}/problems`);
    const goalsByProblem = await Promise.all(
      problems.map(async (problem) => {
        const [actions, tasks]: [CarePlanAction[], CarePlanTask[]] = await Promise.all([
          this.once<CarePlanAction>(`clinical_care_plans/${plan.id}/problems/${problem.id}/actions`),
          this.once<CarePlanTask>(`clinical_care_plans/${plan.id}/problems/${problem.id}/tasks`),
        ]);
        return actions.filter(isOpenGoal).map((action): CarePlanPickerGoal => ({
          problemId: problem.id,
          problemTitle: this.problemTitle(problem),
          goalId: action.id,
          goalText: typeof action.action_title === 'string' ? action.action_title : '',
          interventions: interventionLines(tasks, action.id),
          frozen: false,
        }));
      })
    );
    return {
      carePlanId: plan.id,
      name: typeof plan.name === 'string' ? plan.name : '',
      dxList: Array.isArray(plan.dx_list) ? plan.dx_list.filter((dx) => typeof dx === 'string') : [],
      goals: goalsByProblem.reduce((all: CarePlanPickerGoal[], goals) => all.concat(goals), []),
      frozen: false,
    };
  }

  private once<T>(path: string): Promise<T[]> {
    return firstValueFrom(this.fs.colWithIdsOnce$<T>(path));
  }

  /** The care plan form stores the problem's text as `title`; older problems carry it as `problem`. */
  private problemTitle(problem: CarePlanProblem & { title?: string }): string {
    if (typeof problem.title === 'string') {
      return problem.title;
    }
    return typeof problem.problem === 'string' ? problem.problem : '';
  }
}
